// AI Agent Interaction Component - Direct interaction with AI agents

import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Send,
  Bot,
  User,
  Loader,
  CheckCircle,
  XCircle,
  Brain,
  MessageSquare,
  Zap,
  Settings,
  Trash2
} from 'lucide-react';
import { gtmAgentService } from '../../services/ai/agentService';
import { conversationalAgent, ConversationContext } from '../../services/ai/conversationalAgent';
import { ClaudeConfig } from './ClaudeConfig';
import { AgentContext, AgentExecutionResult, SalesAgentType } from '../../services/ai/agentTypes';

interface AgentInteractionProps {
  className?: string;
  defaultAgent?: SalesAgentType;
  onResult?: (result: AgentExecutionResult) => void;
  preFilledMessage?: string;
}

interface Message {
  id: string;
  type: 'user' | 'agent' | 'system';
  content: string;
  timestamp: Date;
  agentId?: string;
  result?: AgentExecutionResult;
  intent?: string;
  confidence?: number;
  suggestedActions?: string[];
  followUpQuestions?: string[];
}

export const AgentInteraction: React.FC<AgentInteractionProps> = ({
  className = '',
  defaultAgent,
  onResult,
  preFilledMessage
}) => {
  const [messages, setMessages] = useState<Message[]>(() => {
    // Load messages from localStorage on component mount
    const savedMessages = localStorage.getItem('ai-chat-messages');
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        // Convert timestamp strings back to Date objects
        return parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch (error) {
        console.error('Failed to parse saved messages:', error);
      }
    }

    // Return default message if no saved messages or parsing failed
    return [
      {
        id: '1',
        type: 'system',
        content: 'How can I help you close more deals today?',
        timestamp: new Date()
      }
    ];
  });

  const [input, setInput] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(defaultAgent || '');
  const [forceReset, setForceReset] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastProcessedMessage = useRef<string>('');

  const scrollToTop = () => {
    messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    // Only scroll if we explicitly want to (after adding new messages)
    if (shouldAutoScroll) {
      const timer = setTimeout(() => {
        scrollToTop();
        setShouldAutoScroll(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [messages, shouldAutoScroll]);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    localStorage.setItem('ai-chat-messages', JSON.stringify(messages));
  }, [messages]);

  // Auto-send preFilledMessage when provided (moved after conversationMutation definition)
  const autoSendMessage = (message: string) => {
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setShouldAutoScroll(true);
    conversationMutation.mutate(message);
  };

  const resetChat = () => {
    setMessages([
      {
        id: '1',
        type: 'system',
        content: 'How can I help you close more deals today?',
        timestamp: new Date()
      }
    ]);
    setForceReset(true);
  };

  const conversationMutation = useMutation({
    mutationFn: async (userInput: string) => {
      const context: ConversationContext = {
        userId: 'current-user',
        sessionId: `session_${Date.now()}`,
        conversationHistory: messages.map(msg => ({
          id: msg.id,
          type: msg.type as 'user' | 'assistant' | 'system',
          content: msg.content,
          timestamp: msg.timestamp.getTime(),
          intent: msg.intent,
          entities: {},
          agentResult: msg.result
        }))
      };

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out - please try again')), 30000)
      );

      try {
        const result = await Promise.race([
          conversationalAgent.processMessage(userInput, context),
          timeoutPromise
        ]);
        return result;
      } catch (error) {
        console.error('Conversation processing error:', error);
        throw error;
      }
    },
    onSuccess: (response) => {
      const agentMessage: Message = {
        id: `msg_${Date.now()}`,
        type: 'agent',
        content: response.message,
        timestamp: new Date(),
        intent: response.intent,
        confidence: response.confidence,
        result: response.agentResult,
        suggestedActions: response.suggestedActions,
        followUpQuestions: response.followUpQuestions
      };

      setMessages(prev => [...prev, agentMessage]);
      setShouldAutoScroll(true);

      if (response.agentResult) {
        onResult?.(response.agentResult);
      }
    },
    onError: (error) => {
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        type: 'system',
        content: `I encountered an error: ${error instanceof Error ? error.message : 'Please try again'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setShouldAutoScroll(true);
    }
  });

  // Auto-send preFilledMessage when provided
  useEffect(() => {
    if (preFilledMessage && preFilledMessage.trim() && preFilledMessage !== lastProcessedMessage.current) {
      lastProcessedMessage.current = preFilledMessage;
      autoSendMessage(preFilledMessage);
    }
  }, [preFilledMessage]);

  // Reset mutation state when forced
  useEffect(() => {
    if (forceReset) {
      conversationMutation.reset();
      setForceReset(false);
    }
  }, [forceReset, conversationMutation]);

  const handleSendMessage = () => {
    if (!input.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setShouldAutoScroll(true);

    // Process with conversational agent
    conversationMutation.mutate(input);

    setInput('');
  };

  const formatInputForTask = (task: string, input: string) => {
    switch (task) {
      case 'qualify-lead':
        return {
          lead: {
            id: `lead_${Date.now()}`,
            email: extractEmail(input) || 'example@company.com',
            company: extractCompany(input) || 'Sample Company',
            firstName: 'John',
            lastName: 'Doe',
            title: extractTitle(input) || 'Manager'
          }
        };
      case 'analyze-opportunity':
        return {
          opportunity: {
            id: `opp_${Date.now()}`,
            name: input,
            amount: 50000,
            stage: 'Qualification',
            closeDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          }
        };
      case 'compose-email':
        return {
          type: 'follow-up',
          recipientInfo: {
            name: 'John Doe',
            company: 'Sample Company',
            role: 'Manager',
            previousInteractions: []
          },
          context: {
            urgency: 'medium',
            tone: 'professional'
          },
          customRequest: input
        };
      default:
        return { message: input };
    }
  };

  const extractEmail = (text: string): string | null => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
    const match = text.match(emailRegex);
    return match ? match[0] : null;
  };

  const extractCompany = (text: string): string | null => {
    // Simple extraction - look for company keywords
    const companyKeywords = ['Inc', 'LLC', 'Corp', 'Company', 'Ltd'];
    const words = text.split(' ');

    for (let i = 0; i < words.length; i++) {
      if (companyKeywords.some(keyword => words[i].includes(keyword))) {
        return words.slice(Math.max(0, i - 2), i + 1).join(' ');
      }
    }
    return null;
  };

  const extractTitle = (text: string): string | null => {
    const titles = ['Manager', 'Director', 'VP', 'CEO', 'CTO', 'President'];
    const lowerText = text.toLowerCase();

    for (const title of titles) {
      if (lowerText.includes(title.toLowerCase())) {
        return title;
      }
    }
    return null;
  };

  const getMessageIcon = (type: Message['type'], result?: AgentExecutionResult) => {
    switch (type) {
      case 'user':
        return <User className="w-5 h-5 text-blue-600" />;
      case 'agent':
        if (result) {
          return result.success
            ? <CheckCircle className="w-5 h-5 text-green-600" />
            : <XCircle className="w-5 h-5 text-red-600" />;
        }
        return <Bot className="w-5 h-5 text-purple-600" />;
      case 'system':
        return <Settings className="w-5 h-5 text-gray-500" />;
      default:
        return <MessageSquare className="w-5 h-5 text-gray-400" />;
    }
  };

  const getMessageBgColor = (type: Message['type']) => {
    const colors = {
      user: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700',
      agent: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700',
      system: 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
    };
    return colors[type];
  };

  const availableTasks = [
    { id: 'qualify-lead', name: 'Qualify Lead', description: 'Score and qualify a sales lead' },
    { id: 'analyze-opportunity', name: 'Analyze Opportunity', description: 'Assess deal risk and probability' },
    { id: 'compose-email', name: 'Compose Email', description: 'Generate personalized sales email' },
    { id: 'optimize-pipeline', name: 'Optimize Pipeline', description: 'Find pipeline bottlenecks' },
    { id: 'generate-report', name: 'Generate Report', description: 'Create sales analytics report' }
  ];

  return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex flex-col h-full min-h-[600px]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">AI Agent Chat</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Interact with AI agents directly</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={resetChat}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded"
              title="Clear chat"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <ClaudeConfig />
          </div>
        </div>

        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.slice().reverse().map((message, index) => (
            <div
              key={message.id}
              data-message-index={index}
              className={`border rounded-lg p-3 ${getMessageBgColor(message.type)}`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {getMessageIcon(message.type, message.result)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {message.type} {message.agentId && `(${message.agentId})`}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <pre className="whitespace-pre-wrap font-sans">{message.content}</pre>
                  </div>
                  {/* Confidence and intent display */}
                  {(message.confidence || message.intent) && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between text-xs">
                        {message.intent && (
                          <span className="text-gray-500 dark:text-gray-400">
                            Intent: {message.intent}
                          </span>
                        )}
                        {message.confidence && (
                          <span className="text-gray-500 dark:text-gray-400">
                            Confidence: {(message.confidence * 100).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Suggested actions */}
                  {message.suggestedActions && message.suggestedActions.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Suggestions:</div>
                      <div className="flex flex-wrap gap-2">
                        {message.suggestedActions.slice(0, 3).map((action, idx) => (
                          <button
                            key={idx}
                            onClick={() => setInput(action)}
                            className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                          >
                            {action}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Follow-up questions */}
                  {message.followUpQuestions && message.followUpQuestions.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Try asking:</div>
                      <div className="space-y-1">
                        {message.followUpQuestions.slice(0, 2).map((question, idx) => (
                          <button
                            key={idx}
                            onClick={() => setInput(question)}
                            className="block text-xs text-left px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors w-full"
                          >
                            "{question}"
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Agent result details */}
                  {message.result && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400">
                          Agent Execution Time: {message.result.executionTime}ms
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {conversationMutation.isPending && (
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <Loader className="w-5 h-5 text-purple-600 animate-spin" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Agent is processing your request...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex space-x-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="How can I help with your sales today?"
              disabled={false}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
            {conversationMutation.isPending ? (
              <button
                onClick={() => setForceReset(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                title="Cancel current request"
              >
                <XCircle className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            ) : (
              <button
                onClick={handleSendMessage}
                disabled={!input.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>Send</span>
              </button>
            )}
          </div>

          {selectedTask && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {availableTasks.find(t => t.id === selectedTask)?.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};